/**
 * Call Provider
 * Context provider for managing call state across the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { VideoCall, cleanupDailyCall } from './VideoCall';
import { IncomingCallNotification } from './IncomingCallNotification';
import { callService } from '@/services/callService';
import { chatService } from '@/services/chatService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const CallContext = createContext(null);

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callConfig, setCallConfig] = useState({ enabled: false });

  // Load call config
  useEffect(() => {
    const loadConfig = async () => {
      if (!user) return;
      try {
        const config = await callService.getConfig();
        setCallConfig(config);
      } catch (e) {
        console.log('Call config not available');
      }
    };
    loadConfig();
  }, [user]);

  // Check for existing active call on mount
  useEffect(() => {
    const checkActiveCall = async () => {
      if (!user) return;
      try {
        const { active_call } = await callService.getActiveCall();
        if (active_call && active_call.status === 'active') {
          // Rejoin active call
          const joinData = await callService.joinCall(active_call.id);
          setActiveCall({
            callId: active_call.id,
            roomUrl: joinData.room_url,
            token: joinData.token,
            callType: active_call.call_type
          });
        }
      } catch (e) {
        console.log('No active call');
      }
    };
    checkActiveCall();
  }, [user]);

  // Listen for incoming call events via WebSocket
  useEffect(() => {
    if (!user) return;

    const handleIncomingCall = (data) => {
      console.log('[Call] Incoming call:', data);
      setIncomingCall({
        callId: data.call_id,
        roomUrl: data.room_url,
        roomName: data.room_name,
        callerId: data.caller_id,
        callerName: data.caller_name,
        callType: data.call_type || 'video'
      });
    };

    const handleCallAnswered = (data) => {
      console.log('[Call] Call answered:', data);
      toast.success(`${data.answered_by_name || 'User'} joined the call`);
    };

    const handleCallDeclined = (data) => {
      console.log('[Call] Call declined:', data);
      toast.info('Call was declined');
      setActiveCall(null);
    };

    const handleCallEnded = (data) => {
      console.log('[Call] Call ended:', data);
      if (data.duration_seconds) {
        toast.info(`Call ended - Duration: ${Math.floor(data.duration_seconds / 60)}:${(data.duration_seconds % 60).toString().padStart(2, '0')}`);
      } else {
        toast.info('Call ended');
      }
      setActiveCall(null);
    };

    // Subscribe to call events via chat WebSocket
    const unsubscribers = [
      chatService.on('incoming_call', handleIncomingCall),
      chatService.on('call_answered', handleCallAnswered),
      chatService.on('call_declined', handleCallDeclined),
      chatService.on('call_ended', handleCallEnded),
    ];

    return () => {
      unsubscribers.forEach(unsub => unsub?.());
    };
  }, [user]);

  // Start a call
  const startCall = useCallback(async ({ clientId, conversationId, callType = 'video' }) => {
    try {
      const result = await callService.initiateCall({
        clientId,
        conversationId,
        callType
      });

      if (result.success) {
        setActiveCall({
          callId: result.call_id,
          roomUrl: result.room_url,
          token: result.token,
          callType: result.call_type
        });
        return result;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start call');
      throw error;
    }
  }, []);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      const result = await callService.joinCall(incomingCall.callId);
      
      setActiveCall({
        callId: incomingCall.callId,
        roomUrl: result.room_url,
        token: result.token,
        callType: incomingCall.callType
      });
      
      setIncomingCall(null);
    } catch (error) {
      toast.error(error.message || 'Failed to join call');
      setIncomingCall(null);
    }
  }, [incomingCall]);

  // Decline incoming call
  const declineCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      await callService.declineCall(incomingCall.callId);
    } catch (error) {
      console.error('Failed to decline call:', error);
    }
    
    setIncomingCall(null);
  }, [incomingCall]);

  // End active call
  const endCall = useCallback(async () => {
    if (!activeCall) return;

    try {
      await callService.endCall(activeCall.callId);
    } catch (error) {
      console.error('Failed to end call:', error);
    }
    
    // Cleanup Daily instance
    await cleanupDailyCall();
    setActiveCall(null);
  }, [activeCall]);

  // Handle call ended from VideoCall component (user clicked leave or left-meeting event)
  const handleCallEnded = useCallback(async () => {
    const currentCall = activeCall;
    setActiveCall(null);
    
    // Cleanup Daily instance
    await cleanupDailyCall();
    
    // End the call on backend
    if (currentCall) {
      callService.endCall(currentCall.callId).catch(err => {
        console.log('End call cleanup:', err.message);
      });
    }
  }, [activeCall]);

  // Handle error in call - just close the UI, don't end the backend call
  const handleCallError = useCallback(async (err) => {
    console.error('Call error:', err);
    toast.error('Call error occurred');
    // Cleanup Daily instance
    await cleanupDailyCall();
    // Just close the UI - don't end backend call as it might not have started properly
    setActiveCall(null);
  }, []);

  const value = {
    callConfig,
    activeCall,
    incomingCall,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    isInCall: !!activeCall,
    hasIncomingCall: !!incomingCall
  };

  return (
    <CallContext.Provider value={value}>
      {children}

      {/* Incoming Call Notification */}
      <IncomingCallNotification
        isOpen={!!incomingCall}
        callerName={incomingCall?.callerName}
        callType={incomingCall?.callType}
        onAccept={acceptCall}
        onDecline={declineCall}
      />

      {/* Active Call Dialog */}
      <Dialog open={!!activeCall} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-4xl h-[80vh] p-0 bg-gray-900 border-gray-700 overflow-hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {activeCall && (
            <VideoCall
              key={activeCall.callId}
              roomUrl={activeCall.roomUrl}
              token={activeCall.token}
              userName={user?.full_name || user?.email || 'User'}
              callType={activeCall.callType}
              onCallEnded={handleCallEnded}
              onError={handleCallError}
            />
          )}
        </DialogContent>
      </Dialog>
    </CallContext.Provider>
  );
}

export default CallProvider;
